import { Row } from '@tanstack/react-table';

import {
  GenerateBillListItem,
  GenerateBillSalaryAdjustmentListItem,
  GenerateBillSubRowType,
} from '../../types';
import {
  calculateSalaryAdjustment,
  isReconciledAgainstSalary,
  isSalaryAdjustmentEmpty,
  isSalaryOnlyBill,
} from '../../utils';
import { useMemberSubRowFigures } from './use-member-sub-row-figures';

/**
 * Whether the member's bill would consist of nothing but their monthly salary.
 *
 * Derived from the reactive figures hook rather than reading `getIsSelected()` directly, so the
 * hint and the row label update the moment a checkbox is toggled.
 */
export const useIsSalaryOnlyBill = (memberRow: Row<GenerateBillListItem>): boolean => {
  const { selectedLines } = useMemberSubRowFigures(memberRow);

  const selectedBillableLinesCount = selectedLines.filter(
    line => isReconciledAgainstSalary(line.type) || line.type === GenerateBillSubRowType.OVERHEAD
  ).length;

  return isSalaryOnlyBill({
    isMonthlySalaryEnabled: memberRow.original.isMonthlySalaryEnabled,
    selectedBillableLinesCount,
  });
};

export interface SalaryAdjustmentValue {
  value: number;
  /** True when the adjustment nets to zero, so the row is inert and won't be billed. */
  isEmpty: boolean;
}

/**
 * Live adjustment amount for a salary-adjustment sub-row, plus whether it nets to zero. Shared
 * by the label / source / hours / rate / total cells so they can't disagree about whether the
 * row is inert.
 */
export const useSalaryAdjustmentValue = (row: Row<GenerateBillListItem>): SalaryAdjustmentValue => {
  const parentRow = row.getParentRow();
  const adjustment = row.original as unknown as GenerateBillSalaryAdjustmentListItem;
  const { reconciledTotal } = useMemberSubRowFigures(parentRow ?? row);

  const value = calculateSalaryAdjustment({
    monthlySalaryAmount: adjustment.monthlySalaryAmount,
    reconciledLinesTotal: reconciledTotal,
  });

  return { value, isEmpty: isSalaryAdjustmentEmpty(value) };
};
